import React, { useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import PropTypes from "prop-types";

// components:
import Modal from "core/Modal";
import ModalButton from "./ModalButton";
import ChooseKit from "./ChooseKit";
import CreateKit from "./CreateKit";
import FooterContent from "./FooterContent";

// icons:
import Add from "core/ReusableIcons/Add";
import AddedToList from "core/ReusableIcons/AddedToList";

// queries
import { GET_KITS_DATA } from "./queries";

// helpers:
import { getSavedToKit } from "./helpers";

const Kits = ({ iconId, isModalOpen, setModalOpen }) => {
    const [showChooseKit, setShowChooseKit] = useState(true);
    const [showCreateKit, setShowCreateKit] = useState(false);

    const { loading, data } = useQuery(GET_KITS_DATA, {
        fetchPolicy: "cache-and-network",
        variables: {
            iconId
        }
    });

    if (loading) {
        return (
            <ModalButton
                isProcessing
                tooltipMessage="Loading Kits"
                icon={<Add />}
            />
        );
    }

    const {
        kitOwners = {},
        currentUser,
        kits: { items: allKits },
        kitIdsWithIcon = []
    } = data;
    const { hasSubscription = false, id: userId } = currentUser || {};

    const openModal = e => {
        e.preventDefault();
        setModalOpen(true);
    };

    const closeModal = e => {
        e.preventDefault();
        setModalOpen(false);

        // delay resetting modal screen so user
        // doesnt see it switch while modal is in
        // process of closing:
        setTimeout(() => {
            setShowCreateKit(false);
            setShowChooseKit(true);
        }, 500);
    };

    const handleShowChooseKit = () => {
        setShowCreateKit(false);
        setShowChooseKit(true);
    };

    const handleShowCreateKit = () => {
        setShowCreateKit(true);
        setShowChooseKit(false);
    };

    const initialFormData = {
        name_of_kit: "",
        privacy: "Select Privacy (required)",
        owner: "Select Owner (required)",
        description: ""
    };

    const favoritesKit =
        allKits &&
        allKits.length > 0 &&
        allKits.filter(item => {
            return item.name === "Favorites";
        });

    const isSavedToKit = getSavedToKit({
        hasSubscription,
        allKits,
        kitIdsWithIcon
    });

    const modalTriggerText = isSavedToKit ? "Saved to a Kit" : "Add to Kit";
    const modalTriggerIcon = isSavedToKit ? <AddedToList /> : <Add />;

    const modalHeading = showCreateKit ? "Create a New Kit" : "Choose a Kit";

    const kitsToChooseFrom = hasSubscription ? allKits : favoritesKit;

    const footerContent = showChooseKit ? (
        <FooterContent
            isFreeUser={!hasSubscription}
            handleFooterButtonClick={handleShowCreateKit}
        />
    ) : null;

    return (
        <>
            <ModalButton
                tooltipMessage={modalTriggerText}
                isToggled={isSavedToKit}
                icon={modalTriggerIcon}
                onClick={openModal}
            />

            <Modal
                type="kit"
                open={isModalOpen}
                onClose={closeModal}
                handleClose={closeModal}
                heading={modalHeading}
                footerChildren={footerContent}
            >
                {showChooseKit && !showCreateKit && (
                    <ChooseKit
                        iconId={iconId}
                        kits={kitsToChooseFrom}
                        kitIdsWithIcon={kitIdsWithIcon}
                    />
                )}

                {!showChooseKit && showCreateKit && (
                    <CreateKit
                        initialForm={initialFormData}
                        iconId={iconId}
                        userId={userId}
                        ownedTeams={kitOwners.items}
                        handleShowChooseKit={handleShowChooseKit}
                        allKits={allKits}
                    />
                )}
            </Modal>
        </>
    );
};

Kits.propTypes = {
    iconId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    setModalOpen: PropTypes.func.isRequired
};

export default Kits;
